import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, stats, milestones, faqs } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9ottf2qs38sj6jc7fsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // continue
      }

      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const serverMsg =
        (typeof parsed?.meta === 'object' && parsed?.meta && typeof (parsed.meta as Record<string, unknown>).message === 'string'
          ? (parsed.meta as Record<string, string>).message
          : '') ||
        (typeof parsed?.message === 'string' ? parsed.message : '') ||
        '';

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const isSpam = serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');
        setFormStatus('error');
        setFormError(isSpam ? '提交失败，请稍后再试。' : (serverMsg || '提交失败，请稍后再试。'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查后重试。');
    }
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫大全 - 韩国漫画在线阅读平台',
    description: '韩漫大全是一个专注于韩国漫画的在线阅读平台，通过韩漫官方网址提供无删减漫画免费在线观看服务。',
    url: 'https://www.hanman123.com/about',
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: '韩漫大全',
      description: '韩国漫画在线阅读平台',
      foundingDate: '2018',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 韩漫大全 - 韩国漫画在线阅读平台"
        description="韩漫大全是专注于韩国漫画的在线阅读平台，通过韩漫官方网址提供无删减漫画免费在线观看服务。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="韩漫大全,韩国漫画,关于我们,韩漫官方网址,无删减漫画免费在线观看"
        canonicalUrl="https://www.hanman123.com/about"
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Page Header */}
          <div className="py-10 text-center">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
              关于韩漫大全
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
              通过<strong>韩漫官方网址</strong>访问韩国漫画平台，提供<strong>韩漫大全</strong>和<strong>好看韩国漫画APP</strong>，支持<strong>无删减漫画免费在线观看</strong>
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="pb-14">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫大全成立于2018年，是国内最早一批专注于韩国漫画的在线阅读平台。我们致力于为广大中文读者提供高质量、无删减的韩国漫画内容，让每一位喜欢韩漫的读者都能享受到原汁原味的阅读体验。
                  </p>
                  <p>
                    经过多年的发展，韩漫大全已经从一个小型漫画分享站成长为拥有超过<strong>10,000部漫画作品</strong>、每日<strong>50万活跃用户</strong>的综合性韩国漫画平台。我们与韩国多家顶级漫画工作室建立了深度合作关系，确保平台内容的质量和更新速度。
                  </p>
                  <p>
                    2020年，我们推出了<strong>好看韩国漫画APP</strong>，实现了从PC端到移动端的全覆盖，让用户可以随时随地畅享韩国漫画阅读。APP支持离线缓存、夜间模式、智能推荐等实用功能，深受用户喜爱。
                  </p>
                  <p>
                    我们的使命是让每一个喜欢韩国漫画的人都能方便地找到想看的作品，享受流畅、高品质的阅读体验。<strong>无删减漫画免费在线观看</strong>始终是我们对用户的核心承诺。
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20a%20cozy%20reading%20space%20with%20open%20books%20and%20soft%20warm%20amber%20lighting%20minimalist%20elegant%20flat%20illustration%20style%20with%20terracotta%20and%20cream%20color%20palette%20digital%20art%20clean%20composition&width=640&height=640&seq=about-brand-01&orientation=squarish"
                    alt="韩漫大全品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="pb-14">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="space-y-0">
              {milestones.map((m, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-accent-500 flex-shrink-0 mt-1.5"></div>
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-background-200 mt-1"></div>
                    )}
                  </div>
                  <div className={`pb-6 ${index === milestones.length - 1 ? '' : ''}`}>
                    <span className="text-xs font-bold text-accent-600 bg-accent-100/80 px-2 py-0.5 rounded mb-1.5 inline-block">
                      {m.year}
                    </span>
                    <p className="text-sm text-foreground-600">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="pb-14">
            <div className="bg-background-100 rounded-2xl p-8 md:p-10">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-accent-100/80 rounded-xl">
                      <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                    </span>
                    <p className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="pb-14">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100 rounded-xl p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 font-bold text-sm">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="pb-14">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">客服邮箱</p>
                      <a href="mailto:support@hanman123.com" className="text-sm text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer">
                        support@hanman123.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80">
                      <i className="ri-customer-service-line text-accent-600 text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">工作时间</p>
                      <p className="text-sm text-foreground-800">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm text-foreground-800">中国广东省深圳市南山区科技园</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                    给我们留言
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div className="anti-spam-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        您的称呼
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        '提交留言'
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg px-4 py-2.5">
                        <i className="ri-checkbox-circle-line"></i>
                        留言已成功提交，我们会尽快回复您！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2.5">
                        <i className="ri-error-warning-line"></i>
                        {formError || '提交失败，请稍后重试。'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="pb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="space-y-3 max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-bold font-heading text-foreground-800 pr-4">
                      {faq.question}
                    </h3>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform">
                      <i className="ri-add-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}